import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiCheckCircle, FiCircle, FiAlertCircle } from 'react-icons/fi';

interface ChecklistItem {
  id: string;
  category: string;
  task: string;
  completed: boolean;
  dueDate?: string;
  priority: 'high' | 'medium' | 'low';
}

const MyChecklistPage: React.FC = () => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChecklist = async () => {
      if (!currentUser) return;

      try {
        const checklistRef = collection(db, 'checklist');
        const q = query(checklistRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        const checklistData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as ChecklistItem[];

        setChecklist(checklistData);
      } catch (error) {
        console.error('Error fetching checklist:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, [currentUser]);

  const groupedChecklist = checklist.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  const completedCount = checklist.filter(item => item.completed).length;
  const totalCount = checklist.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">My Checklist</h1>
          <p className="text-gray-600 mt-2">Track your tasks and requirements</p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-uc-navy">Overall Progress</h2>
            <span className="text-2xl font-bold text-uc-navy">{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-uc-gold h-4 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {completedCount} of {totalCount} tasks completed
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uc-navy"></div>
            <p className="mt-2 text-gray-600">Loading checklist...</p>
          </div>
        ) : checklist.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FiCheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <p className="text-gray-600">No checklist items found</p>
            <p className="text-sm text-gray-500 mt-2">All tasks are completed!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedChecklist).map(([category, items]) => (
              <div key={category} className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-uc-navy mb-4 font-header uppercase">
                  {category}
                </h2>
                <div className="space-y-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start p-4 rounded-lg border-2 ${
                        item.completed
                          ? 'bg-green-50 border-green-200'
                          : item.priority === 'high'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="mr-3 mt-1">
                        {item.completed ? (
                          <FiCheckCircle size={20} className="text-green-600" />
                        ) : item.priority === 'high' ? (
                          <FiAlertCircle size={20} className="text-red-600" />
                        ) : (
                          <FiCircle size={20} className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${item.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {item.task}
                        </p>
                        {item.dueDate && (
                          <p className="text-sm text-gray-600 mt-1">
                            Due: {new Date(item.dueDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {item.priority === 'high' && !item.completed && (
                        <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                          High Priority
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyChecklistPage;
